import { TableQueryFilter } from "@/Contracts";
import { getTableQueryFilters } from "@/Contracts/Utils";

const URL_PARAM_FILTER_KEYS: ReadonlySet<string> = new Set([
	"sort",
	"page",
	"limit"
]);

export const OPPORTUNITY_ATLAS_SAFE_FIELDS: ReadonlySet<string> = new Set([
	"company_id",
	"archived",
	"deleted",
	"name",
	"client_company_id"
]);

export const ITEM_ATLAS_SAFE_FIELDS: ReadonlySet<string> = new Set([
	"company_id",
	"archived",
	"deleted",
	"description",
	"manufacturer_part_no",
	"name",
	"all_vendor_sku_no",
	"manufacturer",
	"vendor_name"
]);

export const EMAIL_TEMPLATE_ATLAS_SAFE_FIELDS: ReadonlySet<string> = new Set([
	"company_id",
	"name",
	"deleted",
	"body",
	"subject",
	"file_details"
]);

export interface BuildSearchableQueryStringParams {
	readonly filters: TableQueryFilter[];
	readonly keywords: string | null | undefined;
	readonly safeFields: ReadonlySet<string>;
	readonly modelLabel: string;
}

const formatUnsafeFilterError = (
	modelLabel: string,
	unsafeKeys: string[]
): string =>
	`[buildSearchableQueryString] Combining q= with unindexed filter(s) on ` +
	`${modelLabel}: ${unsafeKeys.join(", ")}. Atlas Search cannot evaluate ` +
	`these fields — add them to the model's atlasSearchIndexes or drop the ` +
	`filter when searching.`;

export const buildSearchableQueryString = (
	params: BuildSearchableQueryStringParams
): string => {
	const { filters, keywords, safeFields, modelLabel } = params;
	const hasKeywords = !!keywords && keywords.length > 0;

	let effectiveFilters = filters;
	if (hasKeywords && filters.length > 0) {
		const unsafe = filters.filter(
			filter =>
				filter.filterEnable === true &&
				!URL_PARAM_FILTER_KEYS.has(String(filter.filterKey)) &&
				!safeFields.has(String(filter.filterKey))
		);
		if (unsafe.length > 0) {
			const unsafeKeys = unsafe.map(filter => String(filter.filterKey));
			const message = formatUnsafeFilterError(modelLabel, unsafeKeys);
			if (process.env.NODE_ENV !== "production") {
				throw new Error(message);
			}
			console.error(message);
			const unsafeSet = new Set(unsafe);
			effectiveFilters = filters.filter(filter => !unsafeSet.has(filter));
		}
	}

	const filterString =
		effectiveFilters.length > 0
			? getTableQueryFilters(effectiveFilters, false)
			: "";

	const segments: string[] = [];
	if (filterString.length > 0 && filterString !== "filters=") {
		segments.push(filterString);
	}
	if (hasKeywords) {
		segments.push(`q=${encodeURIComponent(keywords)}`);
	}

	return segments.join("&");
};
